import { Flex, Spacer } from "@hackthenorth/north";
import { Card, Link, Text } from "north.js";
import React, { useEffect, useState } from "react";
import styled from "styled-components";

import { EventActivity } from "src/api/types.generated";
import { Header, PageWrapper } from "src/shared/components";
import { EVENT_ID } from "src/shared/constants/event";
import { Route } from "src/shared/constants/route";
import { useUserContext } from "src/shared/contexts";
import { useDeviceSize } from "src/shared/hooks";

import MentorShiftModal from "../../shared/components/MentorShiftModal";
import { useMyMentorShiftsQuery } from "../graphql/myMentorShifts.generated";

import ShiftCard from "./ShiftCard";

const MentorDashboard: React.FC = () => {
  const isTabletOrSmaller = useDeviceSize("tablet");

  const [selectedShift, setSelectedShift] = React.useState<number | null>(null);
  const [viewModal, setViewModal] = React.useState<boolean>(false);

  const handleShiftClick = (shift: any) => {
    setSelectedShift(shift.id);
    setViewModal(true);
  };

  const [upcomingShifts, setUpcomingShifts] =
    useState<
      Pick<
        EventActivity,
        "id" | "start_time" | "end_time" | "name" | "location"
      >[]
    >();
  const [completedShifts, setCompletedShifts] =
    useState<
      Pick<
        EventActivity,
        "id" | "start_time" | "end_time" | "name" | "location"
      >[]
    >();

  const { info } = useUserContext();
  const userName = info?.name.split(" ")[0] ?? "Mentor";

  const { data: shiftSignupsData, loading: fetchingShiftSignupsData } =
    useMyMentorShiftsQuery({
      variables: {
        event_id: EVENT_ID,
      },
    });

  useEffect(() => {
    if (shiftSignupsData && !fetchingShiftSignupsData) {
      const currentTime = new Date().toISOString();
      const tempUpcomingShifts = shiftSignupsData.myMentorShifts.filter(
        (shift) => shift.end_time > currentTime
      );
      const tempCompletedShifts = shiftSignupsData.myMentorShifts.filter(
        (shift) => shift.end_time <= currentTime
      );
      setUpcomingShifts(tempUpcomingShifts);
      setCompletedShifts(tempCompletedShifts);
    }
  }, [fetchingShiftSignupsData, shiftSignupsData]);

  if (!upcomingShifts || !completedShifts) return null;

  return (
    <>
      <Header title="Mentor Dashboard" subtitle="View and manage my shifts" />
      <PageWrapper>
        {selectedShift && (
          <MentorShiftModal
            shiftId={selectedShift}
            open={viewModal}
            setOpen={setViewModal}
          />
        )}
        <HeaderText>Hi, {userName}!</HeaderText>
        <Text type="h3">
          Thanks for signing up as a mentor for Hack the North!<br></br>
        </Text>
        <Spacer height={12} />
        <Text>
          Make sure to create your{" "}
          <Link href={Route.MENTOR_PROFILE}>Mentor Profile</Link> and check out{" "}
          <Link href={Route.MENTOR_REQUESTS}>Open Requests</Link> to get
          started.
        </Text>
        <Spacer height={64} />
        <CardContainer>
          {isTabletOrSmaller ? (
            <StyledCardMobileContainer>
              <StyledCardMobile>
                <StyleCardMobileTitle>UPCOMING</StyleCardMobileTitle>
                <StyledCardMobileFlex>
                  <Text type="h1">{upcomingShifts.length}</Text>{" "}
                  <StyleCardMobileSubtitle>shifts</StyleCardMobileSubtitle>
                </StyledCardMobileFlex>
              </StyledCardMobile>
              <StyledCardMobile>
                <StyleCardMobileTitle>COMPLETED</StyleCardMobileTitle>
                <StyledCardMobileFlex>
                  <Text type="h1">{completedShifts.length}</Text>{" "}
                  <StyleCardMobileSubtitle>shifts</StyleCardMobileSubtitle>
                </StyledCardMobileFlex>
              </StyledCardMobile>
            </StyledCardMobileContainer>
          ) : (
            <>
              <StyledCard>
                <LargeBodyBold>{upcomingShifts.length}</LargeBodyBold>
                <StyledH3>upcoming shifts</StyledH3>
              </StyledCard>
              <StyledCard>
                <LargeBodyBold>{completedShifts.length}</LargeBodyBold>
                <StyledH3>completed shifts</StyledH3>
              </StyledCard>
            </>
          )}
        </CardContainer>
        <Spacer height={40} />
        <div className="text-text-tertiary mb-4">
          All times are in Eastern Time (EDT)
        </div>
        <Flex column>
          <Text type="h1">Upcoming Shifts</Text>
          {upcomingShifts.length === 0 ? (
            <>
              <Spacer height={24} />
              <Text>You have no upcoming shifts</Text>
            </>
          ) : (
            <CardsContainer>
              {upcomingShifts.map((shift) => (
                <ShiftCard
                  key={shift.id}
                  shiftName={shift.name}
                  startTime={shift.start_time}
                  endTime={shift.end_time}
                  shiftLocation={shift.location!}
                  onClick={() => handleShiftClick(shift)}
                />
              ))}
            </CardsContainer>
          )}
        </Flex>
        <Spacer height={40} />
        <Flex column>
          <Text type="h1">Completed Shifts</Text>
          {completedShifts.length === 0 ? (
            <>
              <Spacer height={24} />
              <Text>You have no completed shifts</Text>
            </>
          ) : (
            <CardsContainer>
              {completedShifts.map((shift) => (
                <ShiftCard
                  key={shift.id}
                  shiftName={shift.name}
                  startTime={shift.start_time}
                  endTime={shift.end_time}
                  shiftLocation={shift.location!}
                  onClick={() => handleShiftClick(shift)}
                />
              ))}
            </CardsContainer>
          )}
        </Flex>
      </PageWrapper>
    </>
  );
};

const HeaderText = styled.h1`
  color: #000;
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.heading};
  font-size: 34px;
  font-style: normal;
  font-weight: 900;
  line-height: normal;
  margin-bottom: 24px;
`;

const CardContainer = styled.div`
  display: flex;
  width: 100%;
  max-width: 1085px;
  justify-content: start;
  gap: 24px;
  align-items: stretch;
`;

const StyledCard = styled(Card)`
  display: flex;
  width: 32%;
  padding: 16px 24px 32px 24px;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  flex-shrink: 0;
`;

const StyledCardMobile = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledCardMobileFlex = styled.div`
  display: flex;
  flex-direction: row;
  align-items: baseline;
  gap: 5px;
`;

const StyledCardMobileContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: start;
  width: 100%;
  gap: 16px;
`;

const StyleCardMobileSubtitle = styled(Text)`
  font-weight: 500 !important;
  font-size: 13px !important;

  color: ${({ theme }) => theme.globalConstants.color.textSecondary} !important;
`;

const StyleCardMobileTitle = styled(Text)`
  font-weight: 500 !important;
  font-size: 13px !important;

  color: ${({ theme }) => theme.globalConstants.color.textBody} !important;
`;

const LargeBodyBold = styled.p`
  color: ${({ theme }) => theme.globalConstants.color.textBody};
  font-size: 64px;
  font-style: normal;
  font-weight: 900;
  line-height: normal;
`;

const StyledH3 = styled.h3`
  overflow: hidden;
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
  text-overflow: ellipsis;

  /* North/Text/H3 */
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.heading};
  font-size: 20px;
  font-style: normal;
  font-weight: 900;
  line-height: normal;
`;

const CardsContainer = styled.div`
  padding: 16px 16px 16px 0px;
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;

export default MentorDashboard;
